import authService from './authService';
import {
  DriverRoute,
  DriverRouteFormData,
  DriverRouteSearchModel,
  SelectableSalesOrder,
  SelectablePickupOrder,
  LiveTrackingResponse,
  OrderDeliveryTracking,
} from '../types/driverRoute';
import { PaginatedResponse, ApiResponse } from '../types/common';

class DriverRouteService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  // ─── Routes CRUD ────────────────────────────────────────────

  async getAll(searchModel: DriverRouteSearchModel): Promise<PaginatedResponse<DriverRoute>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { search: searchModel.searchName }),
      ...(searchModel.driver_id !== undefined && { driver_id: searchModel.driver_id.toString() }),
      ...(searchModel.has_pending_stops !== undefined && { has_pending_stops: searchModel.has_pending_stops.toString() }),
      ...(searchModel.order_id !== undefined && { order_id: searchModel.order_id.toString() }),
      ...(searchModel.customer_name && { customer_name: searchModel.customer_name }),
      ...(searchModel.orderBy && { orderBy: searchModel.orderBy }),
      ...(searchModel.orderDirection && { orderDirection: searchModel.orderDirection }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/driver-routes?${params}`);
    if (!response.ok) throw new Error('Falha ao buscar rotas');

    const json = await response.json();
    // A API responde { success, data, pagination } com a paginação em snake_case
    // (page, page_size, total_items, total_pages) no NÍVEL RAIZ. Ler só as chaves
    // camelCase fazia total_pages virar o default 1 e a tela nunca oferecer a
    // página seguinte. Os fallbacks aninhados/camelCase ficam por compatibilidade.
    const pagination = json.pagination ?? json.data?.pagination;
    return {
      success: true,
      data: Array.isArray(json.data?.data) ? json.data.data : (Array.isArray(json.data) ? json.data : []),
      pagination: {
        page: pagination?.page ?? searchModel.page,
        limit: pagination?.page_size ?? pagination?.limit ?? searchModel.pageSize,
        total: pagination?.total_items ?? pagination?.total ?? 0,
        totalPages: pagination?.total_pages ?? pagination?.totalPages ?? 1,
      },
    };
  }

  async getById(id: number): Promise<DriverRoute> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/driver-routes/${id}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Rota não encontrada');
      throw new Error('Falha ao buscar rota');
    }
    const data: ApiResponse<DriverRoute> = await response.json();
    return data.data!;
  }

  async create(formData: DriverRouteFormData): Promise<DriverRoute> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/driver-routes', formData);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao criar rota');
    }
    const data: ApiResponse<DriverRoute> = await response.json();
    return data.data!;
  }

  async update(id: number, formData: Partial<DriverRouteFormData>): Promise<DriverRoute> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/driver-routes/${id}`, formData);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Rota não encontrada');
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao atualizar rota');
    }
    const data: ApiResponse<DriverRoute> = await response.json();
    return data.data!;
  }

  async delete(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/driver-routes/${id}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Rota não encontrada');
      throw new Error('Falha ao excluir rota');
    }
  }
  // ─── Optimize route ─────────────────────────────────────────

  async optimizeRoute(id: number): Promise<DriverRoute> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/driver-routes/${id}/optimize`, {});
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao otimizar rota');
    }
    const data: ApiResponse<DriverRoute> = await response.json();
    return data.data!;
  }
  // ─── Start delivery (set VehicleKm and optionally change Vehicle) ──

  async startDelivery(id: number, data: { vehicle_km: number; vehicle_id?: number }): Promise<DriverRoute> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/driver-routes/${id}/start-delivery`, data);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao iniciar entrega');
    }
    const result: ApiResponse<DriverRoute> = await response.json();
    return result.data!;
  }

  // ─── Mark stop as delivered ─────────────────────────────────

  async markStopDelivered(routeId: number, stopId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/driver-routes/${routeId}/stops/${stopId}/deliver`, {});
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao marcar parada como entregue');
    }
  }

  /**
   * Marca uma parada de ENTREGA (tipo E) como entregue, enviando dados de
   * tracking (contato, documento, fotos de evidência) via multipart/form-data.
   *
   * Endpoint: PATCH /api/driver-routes/:routeId/stops/:stopId/deliver
   * Content-Type: multipart/form-data
   */
  async markStopDeliveredWithTracking(
    routeId: number,
    stopId: number,
    data: {
      delivery_contact: string;
      delivery_contact_document?: string;
      evidence_photo_1?: File | null;
      evidence_photo_2?: File | null;
    },
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const fd = new FormData();
    fd.append('delivery_contact', data.delivery_contact);
    if (data.delivery_contact_document) {
      fd.append('delivery_contact_document', data.delivery_contact_document);
    }
    if (data.evidence_photo_1) {
      fd.append('evidence_photo_1', data.evidence_photo_1);
    }
    if (data.evidence_photo_2) {
      fd.append('evidence_photo_2', data.evidence_photo_2);
    }

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/driver-routes/${routeId}/stops/${stopId}/deliver`, {
        method: 'PATCH',
        body: fd,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    // 1ª tentativa: cookie de sessão (HTTP-only) enviado via credentials: 'include'.
    let response = await doFetch();

    // 2ª tentativa: token MSAL em cache (cobre o caso de o cookie ter expirado).
    if (response.status === 401) {
      const cachedToken = await authService.getAuthToken(false);
      if (cachedToken) {
        response = await doFetch(cachedToken);
      }
    }

    // 3ª tentativa: refresh FORÇADO via refresh token + re-grava o cookie de sessão.
    // Este endpoint multipart não passa pelo interceptor de getApiClient(), então
    // precisamos escalar para o refresh forte aqui — sem isso, um token expirado
    // (celular em repouso durante a navegação) derrubava a confirmação de entrega.
    if (response.status === 401) {
      const freshToken = await authService.ensureFreshSession();
      if (freshToken) {
        response = await doFetch(freshToken);
      }
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      // Propaga o status para o chamador decidir se preserva o rascunho (401).
      const error = new Error(body.message || `Falha ao confirmar entrega (${response.status})`) as Error & { status?: number };
      error.status = response.status;
      throw error;
    }
  }

  // ─── Selectable data for route creation ─────────────────────

  async updateStopCoordinates(routeId: number, stopId: number, latitude: number, longitude: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/driver-routes/${routeId}/stops/${stopId}/coordinates`, { latitude, longitude });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao atualizar coordenadas');
    }
  }

  /** Próxima entrega: cria o link público de rastreamento e manda o WhatsApp ao cliente (RN-ROTA-45). */
  async startLiveTracking(routeId: number, stopId: number): Promise<LiveTrackingResponse> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/driver-routes/${routeId}/stops/${stopId}/live-tracking`, {});
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao iniciar a entrega com rastreamento');
    }
    const data: ApiResponse<LiveTrackingResponse> = await response.json();
    return data.data!;
  }

  async resendLiveTracking(routeId: number, stopId: number): Promise<LiveTrackingResponse> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/driver-routes/${routeId}/stops/${stopId}/live-tracking/resend`, {});
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao reenviar o link');
    }
    const data: ApiResponse<LiveTrackingResponse> = await response.json();
    return data.data!;
  }

  /** Links de rastreamento e entregas suspensas de um pedido (card da tela do pedido). */
  async getOrderDeliveryTracking(orderId: number): Promise<OrderDeliveryTracking> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/orders/${orderId}/delivery-tracking`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao buscar o rastreamento da entrega');
    }
    const data: ApiResponse<OrderDeliveryTracking> = await response.json();
    return data.data!;
  }

  /** Entrega não realizada: a parada fica suspensa e o pedido volta ao painel (RN-ROTA-48). */
  async suspendStop(routeId: number, stopId: number, reason: string, notifyCustomer: boolean): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/driver-routes/${routeId}/stops/${stopId}/suspend`, {
      reason,
      notify_customer: notifyCustomer,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao suspender a entrega');
    }
  }

  async updateStopETA(routeId: number, stopId: number, estimateDelayInMinutes: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/driver-routes/${routeId}/stops/${stopId}/eta`, { estimate_delay_in_minutes: estimateDelayInMinutes });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao atualizar previsão de chegada');
    }
  }

  async reorderStops(routeId: number, stopIds: number[]): Promise<DriverRoute> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/driver-routes/${routeId}/reorder`, { stop_ids: stopIds });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao reordenar paradas');
    }
    const data: ApiResponse<DriverRoute> = await response.json();
    return data.data!;
  }

  async getPendingPickupOrders(page: number = 1, limit: number = 20): Promise<PaginatedResponse<SelectablePickupOrder>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      is_delivered: 'false',
      orderBy: 'created_at',
      orderDirection: 'desc',
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/goods-pickup-orders?${params}`);
    if (!response.ok) throw new Error('Falha ao buscar ordens de retirada');

    const json = await response.json();
    return {
      success: true,
      data: Array.isArray(json.data?.data) ? json.data.data : (Array.isArray(json.data) ? json.data : []),
      pagination: {
        page: json.data?.pagination?.page ?? 1,
        limit: json.data?.pagination?.page_size ?? limit,
        total: json.data?.pagination?.total_items ?? 0,
        totalPages: json.data?.pagination?.total_pages ?? 1,
      },
    };
  }

  async getDeliverySalesOrders(page: number = 1, limit: number = 20): Promise<PaginatedResponse<SelectableSalesOrder>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      order_status_ids: '6,8,10',
      orderBy: 'updated_at',
      orderDirection: 'desc',
      exclude_assigned_to_route: 'true',
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/orders?${params}`);
    if (!response.ok) throw new Error('Falha ao buscar pedidos de venda');

    const json = await response.json();
    return {
      success: true,
      data: Array.isArray(json.data?.data) ? json.data.data : (Array.isArray(json.data) ? json.data : []),
      pagination: {
        page: json.data?.pagination?.page ?? 1,
        limit: json.data?.pagination?.page_size ?? limit,
        total: json.data?.pagination?.total_items ?? 0,
        totalPages: json.data?.pagination?.total_pages ?? 1,
      },
    };
  }

  /**
   * Busca pedidos de venda filtrados por shipping provider (transportadora).
   * Retorna pedidos cujo OrderFreightQuote selecionado (IsSelected=1) pertence ao ProviderId informado.
   * Ordenação por UpdatedAt DESC, paginado.
   */
  async getOrdersByShippingProvider(providerId: number, page: number = 1, limit: number = 20): Promise<PaginatedResponse<SelectableSalesOrder>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      shipping_provider_id: providerId.toString(),
      order_status_ids: '6,8,10',
      orderBy: 'updated_at',
      orderDirection: 'desc',
      exclude_assigned_to_route: 'true',
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/orders?${params}`);
    if (!response.ok) throw new Error('Falha ao buscar pedidos por transportadora');

    const json = await response.json();
    return {
      success: true,
      data: Array.isArray(json.data?.data) ? json.data.data : (Array.isArray(json.data) ? json.data : []),
      pagination: {
        page: json.data?.pagination?.page ?? 1,
        limit: json.data?.pagination?.page_size ?? limit,
        total: json.data?.pagination?.total_items ?? 0,
        totalPages: json.data?.pagination?.total_pages ?? 1,
      },
    };
  }

  // ─── Pending routes (for delivery execution screen) ─────────

  async getPendingRoutes(searchModel: DriverRouteSearchModel): Promise<PaginatedResponse<DriverRoute>> {
    return this.getAll({ ...searchModel, has_pending_stops: true });
  }

  // ─── PDF Download ───────────────────────────────────────────

  private async getBaseUrl(): Promise<string> {
    const { getConfig } = await import('../config');
    return getConfig().api.baseUrl;
  }

  async downloadPdf(id: number): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/driver-routes/${id}/pdf`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar PDF (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `rota-entrega-${id}.pdf`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }
}

export const driverRouteService = new DriverRouteService();
export default driverRouteService;
